import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

/** Меню открыто — Escape должен закрыть меню, а не окно, внутри которого оно лежит. */
let openMenus = 0;
export const menuIsOpen = () => openMenus > 0;

export type PopOption = {
  value: string;
  label?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  /** Почему пункт недоступен — показывается подсказкой. */
  why?: string;
  /** Разделитель перед пунктом. */
  separator?: boolean;
  /** Заголовок раздела вместо пункта (не выбирается). */
  heading?: string;
  danger?: boolean;
};

type Props = {
  trigger: ReactNode;
  options: PopOption[];
  onPick: (value: string) => void;
  disabled?: boolean;
  /** Подсказка, почему всё поле недоступно. */
  why?: string;
  align?: "left" | "right";
  triggerClass?: string;
  ariaLabel?: string;
  footer?: (close: () => void) => ReactNode;
  /** Render outside scrolling/clipping containers (e.g. employee tables). */
  portal?: boolean;
};

/**
 * Небольшое меню выбора: кнопка + список под ней. Управляется с клавиатуры
 * (стрелки, Enter, Esc), закрывается по клику вне, возвращает фокус на кнопку.
 */
export function PopMenu({
  trigger,
  options,
  onPick,
  disabled,
  why,
  align = "left",
  triggerClass = "prop-btn",
  ariaLabel,
  footer,
  portal = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  const [position, setPosition] = useState({ left: 0, top: 0, maxHeight: 0 });

  useLayoutEffect(() => {
    if (!open || !portal) return;
    const place = () => {
      if (!button.current || !menu.current) return;
      const anchor = button.current.getBoundingClientRect();
      const padding = 8;
      const gap = 6;
      const below = window.innerHeight - anchor.bottom - gap - padding;
      const above = anchor.top - gap - padding;
      const naturalHeight =
        menu.current.scrollHeight +
        menu.current.offsetHeight -
        menu.current.clientHeight;
      const upward = naturalHeight > below && above > below;
      const maxHeight = Math.max(0, upward ? above : below);
      const height = Math.min(naturalHeight, maxHeight);
      const width = menu.current.offsetWidth;
      setPosition({
        left: Math.max(
          padding,
          Math.min(
            align === "right" ? anchor.right - width : anchor.left,
            window.innerWidth - width - padding,
          ),
        ),
        top: Math.max(
          padding,
          upward ? anchor.top - gap - height : anchor.bottom + gap,
        ),
        maxHeight,
      });
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(button.current!);
    observer.observe(menu.current!);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, portal, align]);

  useEffect(() => {
    if (!open) return;
    openMenus++;
    const outside = (e: PointerEvent) => {
      if (
        !wrap.current?.contains(e.target as Node) &&
        !menu.current?.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("pointerdown", outside, true);
    menu.current
      ?.querySelector<HTMLElement>("[role^=menuitem]:not([disabled])")
      ?.focus();
    return () => {
      openMenus--;
      document.removeEventListener("pointerdown", outside, true);
    };
  }, [open]);

  const close = (focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger) button.current?.focus();
  };
  const onKey = (e: KeyboardEvent) => {
    // Inputs own their native date/time navigation; Escape still closes the menu.
    if (
      e.key !== "Escape" &&
      (e.target as HTMLElement).closest("input, textarea, select")
    )
      return;
    const items = [
      ...(menu.current?.querySelectorAll<HTMLElement>(
        "[role^=menuitem]:not([disabled])",
      ) || []),
    ];
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab" && !footer) {
      close(false);
    }
  };

  const content = open && (
    <div
      className={"pop-menu " + (portal ? "portal" : align)}
      style={
        portal
          ? {
              left: position.left,
              top: position.top,
              maxHeight: position.maxHeight || undefined,
            }
          : undefined
      }
      id={id}
      role="menu"
      ref={menu}
      onKeyDown={onKey}
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null;
        if (
          next &&
          !menu.current?.contains(next) &&
          !wrap.current?.contains(next)
        )
          close(false);
      }}
    >
      {options.map((o) => (
        <div key={o.value} role="none">
          {o.separator && <div className="pop-sep" role="separator" />}
          {o.heading ? (
            <div className="pop-heading" role="presentation">
              {o.heading}
            </div>
          ) : (
            <button
              type="button"
              role="menuitemradio"
              aria-checked={!!o.selected}
              className={
                "pop-item" +
                (o.selected ? " sel" : "") +
                (o.danger ? " danger" : "")
              }
              disabled={o.disabled}
              title={o.disabled ? o.why : undefined}
              onClick={() => {
                close();
                onPick(o.value);
              }}
            >
              <span>{o.label}</span>
              {o.selected && <Check size={14} aria-hidden="true" />}
            </button>
          )}
        </div>
      ))}
      {footer && <div className="pop-footer">{footer(() => close())}</div>}
    </div>
  );

  return (
    <div className="pop-wrap" ref={wrap}>
      <button
        type="button"
        ref={button}
        className={triggerClass + (disabled ? " locked" : "")}
        disabled={disabled}
        title={disabled ? why : undefined}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {trigger}
      </button>
      {portal && content ? createPortal(content, document.body) : content}
    </div>
  );
}
